/**
 * Production OAuth always completes on the canonical apex. CloudFront enforces
 * that at the edge, but a browser can briefly run a cached app bundle from an
 * older alias. Amplify rightly rejects starting PKCE from that different
 * origin, so hand the user off before calling `signInWithRedirect`.
 */
export const CANONICAL_PRODUCTION_OAUTH_ORIGIN = 'https://trackwith.ai';
export const GOOGLE_OAUTH_RESUME_PARAM = 'twai_oauth_resume';
export const GOOGLE_OAUTH_RESUME_VALUE = 'google';

const LEGACY_PRODUCTION_OAUTH_HOSTS = new Set([
  'www.trackwith.ai',
  'trackwithai.ai',
  'www.trackwithai.ai',
  'd1vdrxulukee9e.cloudfront.net',
]);

/**
 * Return an apex login URL that resumes the already-clicked Google action, or
 * null when the current host may safely start OAuth itself (localhost/staging
 * included). The redirect path stays in the URL because sessionStorage is
 * origin-scoped and cannot cross from a stale alias to the apex.
 */
export function buildCanonicalGoogleOAuthHandoffUrl(
  hostname: string,
  redirectPath: string,
): string | null {
  if (!LEGACY_PRODUCTION_OAUTH_HOSTS.has(hostname.toLowerCase())) {
    return null;
  }

  const handoff = new URL('/login', CANONICAL_PRODUCTION_OAUTH_ORIGIN);
  handoff.searchParams.set('redirect', redirectPath);
  handoff.searchParams.set(GOOGLE_OAUTH_RESUME_PARAM, GOOGLE_OAUTH_RESUME_VALUE);
  return handoff.toString();
}

/** Do not auto-start again after Cognito has returned a code or error. */
export function shouldResumeGoogleOAuth(searchParams: URLSearchParams): boolean {
  return (
    searchParams.get(GOOGLE_OAUTH_RESUME_PARAM) === GOOGLE_OAUTH_RESUME_VALUE
    && !searchParams.has('code')
    && !searchParams.has('error')
  );
}
